import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse } from '../api/helpers';
import { inputStyle, labelStyle, modalWrap, primaryBtn, cancelBtn, successAlert, errorAlert, pillBtn, filterInputStyle } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

const STATUS_COLORS = {
  scheduled:  { bg: 'rgba(14,165,233,0.15)',  text: '#38bdf8' },
  confirmed:  { bg: 'rgba(34,197,94,0.15)',   text: '#4ade80' },
  cancelled:  { bg: 'rgba(239,68,68,0.15)',   text: '#f87171' },
  completed:  { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8' },
};

const TIME_SLOTS = ['09:00','09:30','10:00','10:30','11:00','11:30','12:00','14:00','14:30','15:00','15:30','16:00','16:30'];

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
  return <span style={{ background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{status}</span>;
}

function BookModal({ onClose, onSuccess, hospitalId }) {
  const [form, setForm] = useState({
    patient_name: '', patient_phone: '', patient_id: '',
    doctor_id: 'DOC-001', doctor_name: 'Dr. Mehta',
    appointment_date: new Date().toISOString().split('T')[0],
    time_slot: '10:00', visit_type: 'new', notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      const payload = { ...form, hospital_id: hospitalId, patient_phone: form.patient_phone.startsWith('+') ? form.patient_phone : `+91${form.patient_phone}` };
      if (!payload.patient_id) delete payload.patient_id;
      const res = await axios.post(`${API_URL}/appointments`, payload, { headers });
      onSuccess(parseResponse(res));
    } catch { setError('Failed to book appointment'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Book Appointment</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
        </div>
        {error && <div style={errorAlert}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div><label style={labelStyle}>Patient Name</label><input style={inputStyle} placeholder="Full name" required value={form.patient_name} onChange={e => setForm({...form, patient_name: e.target.value})} /></div>
            <div><label style={labelStyle}>Phone</label><input style={inputStyle} placeholder="9000000000" required value={form.patient_phone} onChange={e => setForm({...form, patient_phone: e.target.value})} /></div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Patient ID (optional)</label>
            <input style={inputStyle} placeholder="HOSP-2026-XXXX" value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div><label style={labelStyle}>Doctor</label><input style={inputStyle} placeholder="Doctor name" required value={form.doctor_name} onChange={e => setForm({...form, doctor_name: e.target.value})} /></div>
            <div>
              <label style={labelStyle}>Visit Type</label>
              <select style={inputStyle} value={form.visit_type} onChange={e => setForm({...form, visit_type: e.target.value})}>
                <option value="new">New Visit</option>
                <option value="follow-up">Follow Up</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div><label style={labelStyle}>Date</label><input style={{ ...inputStyle, colorScheme: 'dark' }} type="date" required value={form.appointment_date} onChange={e => setForm({...form, appointment_date: e.target.value})} /></div>
            <div>
              <label style={labelStyle}>Time Slot</label>
              <select style={inputStyle} value={form.time_slot} onChange={e => setForm({...form, time_slot: e.target.value})}>
                {TIME_SLOTS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, height: 70, resize: 'vertical' }} placeholder="Patient complaint or notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, ...primaryBtn(loading) }}>{loading ? 'Booking…' : 'Book Appointment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RescheduleModal({ appointment, onClose, onSuccess }) {
  const [newDate, setNewDate] = useState(appointment.appointment_date);
  const [newTime, setNewTime] = useState(appointment.time_slot);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newDate === appointment.appointment_date && newTime === appointment.time_slot) { setError('Please select a different date or time.'); return; }
    setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      await axios.put(`${API_URL}/appointments/${appointment.appointment_id}`, { appointment_date: appointment.appointment_date, new_appointment_date: newDate, new_time_slot: newTime }, { headers, params: {} });
      onSuccess(`Rescheduled to ${newDate} at ${newTime}`);
    } catch { setError('Failed to reschedule'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.75)', zIndex: 1100 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>Reschedule Appointment</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
        </div>
        <div style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>Current Schedule</div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>📅 {appointment.appointment_date} &nbsp;⏰ {appointment.time_slot}</div>
        </div>
        {error && <div style={errorAlert}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>New Date</label><input type="date" style={{ ...inputStyle, colorScheme: 'dark' }} required value={newDate} onChange={e => setNewDate(e.target.value)} /></div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>New Time Slot</label>
            <select style={inputStyle} value={newTime} onChange={e => setNewTime(e.target.value)}>{TIME_SLOTS.map(t => <option key={t}>{t}</option>)}</select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, ...primaryBtn(loading), background: loading ? '#1e1a3e' : 'linear-gradient(135deg,#f59e0b,#f97316)' }}>{loading ? 'Rescheduling…' : '📅 Confirm Reschedule'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CancelConfirmModal({ appointment, onClose, onConfirm, loading }) {
  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.75)', zIndex: 1100 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 400 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>Cancel Appointment</div>
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#f87171', fontWeight: 600, marginBottom: 4 }}>Are you sure you want to cancel?</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>{appointment.patient_name} · {appointment.doctor_name}<br />📅 {appointment.appointment_date} ⏰ {appointment.time_slot}</div>
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 20 }}>A cancellation notification will be sent to the patient via WhatsApp.</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Go Back</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 2, padding: '10px', borderRadius: 8, background: loading ? '#1e1a3e' : 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{loading ? 'Cancelling…' : '✕ Cancel Appointment'}</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ appointment, onClose, onUpdateStatus, onReschedule }) {
  const [status, setStatus]               = useState(appointment.status);
  const [loading, setLoading]             = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel]       = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    setLoading(true);
    try { await onUpdateStatus(appointment.appointment_id, appointment.appointment_date, newStatus); setStatus(newStatus); }
    finally { setLoading(false); }
  };

  const handleCancelConfirm = async () => {
    setLoading(true);
    try { await onUpdateStatus(appointment.appointment_id, appointment.appointment_date, 'cancelled'); setStatus('cancelled'); setShowCancel(false); }
    finally { setLoading(false); }
  };

  const isDone = status === 'cancelled' || status === 'completed';
  const detailFields = [
    { label: 'Patient', value: appointment.patient_name }, { label: 'Phone', value: appointment.patient_phone },
    { label: 'Doctor', value: appointment.doctor_name },   { label: 'Visit Type', value: appointment.visit_type },
    { label: 'Date', value: appointment.appointment_date },{ label: 'Time', value: appointment.time_slot },
  ];

  return (
    <>
      <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
        <div className="modal-box" style={{ ...modalWrap, width: 500 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Appointment Details</div>
            <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
          </div>

          <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Appointment ID</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-light)', fontFamily: 'monospace' }}>{appointment.appointment_id}</div>
            </div>
            <StatusBadge status={status} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {detailFields.map((item, i) => (
              <div key={i} style={{ background: 'var(--bg-card-hover)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.value}</div>
              </div>
            ))}
          </div>

          {appointment.notes && (
            <div style={{ background: 'var(--bg-card-hover)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)', marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>{appointment.notes}</div>
            </div>
          )}

          {!isDone && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {['confirmed', 'completed'].map(s => {
                const c = STATUS_COLORS[s];
                return (
                  <button key={s} onClick={() => handleStatusUpdate(s)} disabled={loading || status === s}
                    style={{ padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: status === s ? 'default' : 'pointer', border: 'none', background: status === s ? c.bg : 'rgba(255,255,255,0.06)', color: status === s ? c.text : '#64748b', textTransform: 'capitalize' }}>
                    {s}
                  </button>
                );
              })}
            </div>
          )}

          {!isDone && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <button onClick={() => setShowReschedule(true)} disabled={loading}
                style={{ flex: 1, padding: '9px', borderRadius: 8, background: 'linear-gradient(135deg,#f59e0b,#f97316)', color: '#fff', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                📅 Reschedule
              </button>
              <button onClick={() => setShowCancel(true)} disabled={loading}
                style={{ flex: 1, padding: '9px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                ✕ Cancel
              </button>
            </div>
          )}

          <button onClick={onClose} style={{ width: '100%', ...cancelBtn }}>Close</button>
        </div>
      </div>

      {showReschedule && <RescheduleModal appointment={appointment} onClose={() => setShowReschedule(false)} onSuccess={(msg) => { setShowReschedule(false); onReschedule(msg); }} />}
      {showCancel     && <CancelConfirmModal appointment={appointment} onClose={() => setShowCancel(false)} onConfirm={handleCancelConfirm} loading={loading} />}
    </>
  );
}

export default function Appointments({ user }) {
  const isSuperAdmin                            = user?.role === 'super-admin';
  const [selectedHospital, setSelectedHospital] = useState(isSuperAdmin ? '' : user?.hospital_id);
  const [hospitals, setHospitals]               = useState([]);
  const HOSPITAL_ID                             = selectedHospital;

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBook, setShowBook]         = useState(false);
  const [viewAppt, setViewAppt]         = useState(null);
  const [successMsg, setSuccessMsg]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { if (isSuperAdmin) fetchHospitals(); }, []); // eslint-disable-line
  useEffect(() => { if (HOSPITAL_ID) fetchAppointments(); }, [HOSPITAL_ID, selectedDate]); // eslint-disable-line

  const fetchHospitals = async () => {
    try { const headers = await getAuthHeader(); setHospitals(parseResponse(await axios.get(`${API_URL}/hospitals`, { headers })).hospitals || []); } catch {}
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try { const headers = await getAuthHeader(); setAppointments(parseResponse(await axios.get(`${API_URL}/appointments`, { params: { date: selectedDate }, headers })).appointments || []); }
    catch {} finally { setLoading(false); }
  };

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 5000); };

  const handleUpdateStatus = async (id, date, newStatus) => {
    const headers = await getAuthHeader();
    await axios.put(`${API_URL}/appointments/${id}`, { appointment_date: date, status: newStatus }, { headers, params: {} });
    fetchAppointments();
  };

  const filtered = appointments.filter(a => statusFilter === 'all' ? true : a.status === statusFilter);
  const counts = { all: appointments.length, scheduled: appointments.filter(a => a.status === 'scheduled').length, confirmed: appointments.filter(a => a.status === 'confirmed').length, completed: appointments.filter(a => a.status === 'completed').length, cancelled: appointments.filter(a => a.status === 'cancelled').length };

  const STAT_COLORS = { all: 'var(--accent-light)', scheduled: '#fbbf24', confirmed: '#4ade80', completed: '#94a3b8', cancelled: '#f87171' };
  const selectStyle = { flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', fontSize: 13, color: 'var(--text-primary)', background: 'rgba(255,255,255,0.06)', outline: 'none' };

  return (
    <div>
      {isSuperAdmin && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Hospital:</span>
          <select value={selectedHospital} onChange={e => { setSelectedHospital(e.target.value); setAppointments([]); }} style={selectStyle}>
            <option value="">— Select a hospital —</option>
            {hospitals.map(h => <option key={h.hospital_id} value={h.hospital_id}>{h.name} ({h.hospital_id})</option>)}
          </select>
        </div>
      )}

      {!HOSPITAL_ID && isSuperAdmin ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 14, background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>Select a hospital above</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Date:</span>
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', fontSize: 13, color: 'var(--text-primary)', background: 'rgba(255,255,255,0.06)', outline: 'none', colorScheme: 'dark' }} />
            </div>
            <button onClick={() => setShowBook(true)} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 12px rgba(99,102,241,0.4)', whiteSpace: 'nowrap' }}>
              + Book Appointment
            </button>
          </div>

          <div className="grid-4" style={{ marginBottom: 20 }}>
            {['all','scheduled','confirmed','cancelled'].map(s => (
              <div key={s} className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter(s)}>
                <div style={{ fontSize: 28, fontWeight: 800, color: STAT_COLORS[s], lineHeight: 1, marginBottom: 4 }}>{counts[s]}</div>
                <div style={{ fontSize: 12, color: '#475569', textTransform: 'capitalize' }}>{s}</div>
              </div>
            ))}
          </div>

          {successMsg && <div style={successAlert}>{successMsg}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['all','scheduled','confirmed','completed','cancelled'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} style={pillBtn(statusFilter === s)}>
                {s} ({counts[s] || 0})
              </button>
            ))}
          </div>

          <div className="page-card">
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>Loading appointments…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>No appointments for {selectedDate}</div>
            ) : (
              <div className="table-wrap">
                <table className="dark-table">
                  <thead><tr>{['ID','Patient','Doctor','Time','Visit Type','Status','Actions'].map(h => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filtered.map((a, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent-light)', fontWeight: 600 }}>{a.appointment_id}</td>
                        <td><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{a.patient_name}</div><div style={{ fontSize: 11, color: '#475569' }}>{a.patient_phone}</div></td>
                        <td style={{ fontSize: 12, color: '#94a3b8' }}>{a.doctor_name}</td>
                        <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{a.time_slot}</td>
                        <td style={{ fontSize: 12, color: '#64748b', textTransform: 'capitalize' }}>{a.visit_type}</td>
                        <td><StatusBadge status={a.status} /></td>
                        <td>
                          <button onClick={() => setViewAppt(a)} style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 7, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: 'var(--accent-light)', fontWeight: 600 }}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showBook && <BookModal onClose={() => setShowBook(false)} onSuccess={(data) => { setShowBook(false); showSuccess(`✅ Booked! ID: ${data.appointment_id}`); fetchAppointments(); }} hospitalId={HOSPITAL_ID} />}
      {viewAppt && <ViewModal appointment={viewAppt} onClose={() => setViewAppt(null)} onUpdateStatus={handleUpdateStatus} onReschedule={(msg) => { setViewAppt(null); showSuccess(`✅ ${msg} — WhatsApp notification sent.`); fetchAppointments(); }} />}
    </div>
  );
}
